import React, { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

export const Lightbox = ({
  open,
  images = [],
  index = 0,
  alt = "",
  onClose,
  onIndexChange,
}) => {
  const [zoomed, setZoomed] = useState(false);
  const touchStartX = useRef(null);

  const count = images.length;
  const hasNav = count > 1;

  const go = (dir) => {
    if (!hasNav || !onIndexChange) return;
    setZoomed(false);
    const next = (index + dir + count) % count;
    onIndexChange(next);
  };

  useEffect(() => {
    if (!open) {
      setZoomed(false);
      return;
    }
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") go(1);
      else if (e.key === "ArrowLeft") go(-1);
    };
    document.addEventListener("keydown", onKey);
    const scrollY = window.scrollY;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
      window.scrollTo(0, scrollY);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, index, count]);

  if (!open) return null;

  const src = images[index];

  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null || zoomed) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 45) go(delta < 0 ? 1 : -1);
    touchStartX.current = null;
  };

  return createPortal(
    <div
      data-testid="lightbox"
      role="dialog"
      aria-modal="true"
      aria-label={alt}
      onClick={onClose}
      className="fixed inset-0 z-[200] bg-[#2b2a27]/90 backdrop-blur-sm overflow-auto overscroll-contain"
    >
      <button
        type="button"
        data-testid="lightbox-close"
        aria-label="Fermer"
        onClick={onClose}
        className="fixed top-4 right-4 md:top-6 md:right-6 z-[210] grid place-items-center h-11 w-11 rounded-full bg-white/95 text-[#4b2018] shadow-md hover:bg-white transition-colors"
      >
        <X size={22} />
      </button>

      {hasNav && (
        <>
          <button
            type="button"
            data-testid="lightbox-prev"
            aria-label="Image précédente"
            onClick={(e) => {
              e.stopPropagation();
              go(-1);
            }}
            className="fixed left-3 md:left-6 top-1/2 -translate-y-1/2 z-[210] grid place-items-center h-11 w-11 rounded-full bg-white/85 text-[#4b2018] shadow-md hover:bg-white transition-colors"
          >
            <ChevronLeft size={24} />
          </button>
          <button
            type="button"
            data-testid="lightbox-next"
            aria-label="Image suivante"
            onClick={(e) => {
              e.stopPropagation();
              go(1);
            }}
            className="fixed right-3 md:right-6 top-1/2 -translate-y-1/2 z-[210] grid place-items-center h-11 w-11 rounded-full bg-white/85 text-[#4b2018] shadow-md hover:bg-white transition-colors"
          >
            <ChevronRight size={24} />
          </button>
        </>
      )}

      <div
        className="min-h-full w-full flex items-center justify-center p-4 pt-16 md:p-10"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        <img
          key={index}
          src={src}
          alt={alt}
          data-testid="lightbox-image"
          onClick={(e) => {
            e.stopPropagation();
            setZoomed((z) => !z);
          }}
          className={`select-none rounded-md shadow-2xl ${
            zoomed
              ? "max-w-none w-auto h-auto cursor-zoom-out"
              : "max-h-[85vh] max-w-full object-contain cursor-zoom-in"
          }`}
        />
      </div>
    </div>,
    document.body
  );
};
